import { useEffect } from 'react';
import { isNil } from 'lodash';
import {
  dispatch,
  useAppSelector,
  getToken,
  getIsAuthenticated,
  getWorkspace,
  getUserData,
  useLazyTripClockInHistoryQuery,
  updateClockInInfo,
  useLazyTripCheckInHistoryQuery,
  updateCheckInInfo,
  updateCustomerInfo,
  Address,
  getCheckInInfo,
  UserType,
} from '@zonofi/zono-money-store';
import { offlineQueue, hydrateOfflineQueue } from '../../queue/offline';
import { customApiQueue } from '../../queue/request';
import { redirectionModalRef } from '../../modals';

export const useFieldOpsInitialization = () => {
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const token = useAppSelector(getToken);
  const workspaceDetails = useAppSelector(getWorkspace);
  const workspaceId = workspaceDetails?.id ?? '';
  const userData = useAppSelector(getUserData);
  const { inviteId: teamMemberId = '', inviteUserType = '' } = userData;
  const isUserDataHydrated = Boolean(userData.id || userData.token);
  const checkInInfo = useAppSelector(getCheckInInfo);

  const [fetchTodayClockInHistory] = useLazyTripClockInHistoryQuery();
  const [fetchTodayCheckInHistory] = useLazyTripCheckInHistoryQuery();

  useEffect(() => {
    if (teamMemberId && workspaceId && token && isAuthenticated) {
      fetchTodayClockInHistory({
        sellerWorkspaceId: workspaceId,
        teamMemberId: teamMemberId,
        isActive: true,
      })
        .unwrap()
        .then((data) => {
          const record = data?.records?.[0];
          const activity = record?.dayActivity?.[0];
          if (activity?.clockIn) {
            dispatch(
              updateClockInInfo({
                tripId: activity?.tripId,
                clockIn: activity?.clockIn,
                clockInLocation: activity?.clockInLocation,
                clockOut: activity?.clockOut,
              })
            );
          } else {
            dispatch(updateClockInInfo(undefined));
          }
        })
        .catch((err) => {
          console.log('CLOCK-IN err:', err);
        });
    }
  }, [teamMemberId, token, isAuthenticated, workspaceId]);

  useEffect(() => {
    if (
      workspaceId &&
      token &&
      isAuthenticated &&
      isNil(checkInInfo?.customerId)
    ) {
      fetchTodayCheckInHistory({
        sellerWorkspaceId: workspaceId,
        isActive: true,
      })
        .unwrap()
        .then((data) => {
          const visit = data?.visits?.[0];
          const visitCustomerId = visit?.customerDetails?.id;

          const items = offlineQueue.getSnapshot();
          const hasPendingCheckout = items?.some(
            (item) =>
              item?.type === 'CHECK_OUT' &&
              item?.customerMetadata?.customerId === visitCustomerId
          );

          if (visit?.id && !hasPendingCheckout) {
            dispatch(
              updateCheckInInfo({
                checkInId: visit?.id,
                customerId: visit?.customerDetails?.id,
                checkInTime: visit?.checkInTime,
                checkInLocation: {
                  lat: visit?.checkInLocation?.lat,
                  lng: visit?.checkInLocation?.lng,
                  address: visit?.checkInLocation?.address,
                },
              })
            );

            dispatch(
              updateCustomerInfo({
                customerAddress: visit?.customerDetails
                  ?.physicalAddress as Address,
                customerName:
                  visit?.customerDetails?.companyName ??
                  visit?.customerDetails?.name,
                customerId: visit?.customerDetails?.id,
                phone: visit?.customerDetails?.phone,
              })
            );
          } else {
            dispatch(updateCheckInInfo(undefined));
            dispatch(updateCustomerInfo(undefined));
          }
        })
        .catch((err) => {
          console.log('CHECK-IN err:', err);
        });
    }
  }, [token, isAuthenticated, workspaceId]);

  useEffect(() => {
    hydrateOfflineQueue();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      customApiQueue.cancelAll();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (
      token &&
      isAuthenticated &&
      isUserDataHydrated &&
      inviteUserType !== UserType.SALES
    ) {
      redirectionModalRef.current?.openRedirectionModal();
    }
  }, [token, isAuthenticated, inviteUserType, isUserDataHydrated]);
};
